import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n";
import { PRODUCTS_LIST } from "@/data/products";

export default function Products() {
  const ref = useRef(null);
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [mounted, setMounted] = useState(false);

  // Auto-rotate the spotlight every 4.5s — adds life without animation jank
  useEffect(() => {
    setMounted(true);
    const id = setInterval(() => {
      setActive((a) => (a + 1) % PRODUCTS_LIST.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);

  const product = PRODUCTS_LIST[active];
  const isLightAccent = product.accent === "#d8ff5c" || product.accent === "#b8e637";

  return (
    <div
      ref={ref}
      data-testid="products-root"
      className="relative w-full h-screen overflow-hidden grain text-white"
      style={{ background: "var(--ink)" }}
    >
      {/* Atmospheric orb that takes the active accent colour */}
      <div
        className="absolute rounded-full pointer-events-none transition-all duration-[1400ms] ease-out"
        style={{
          width: 900,
          height: 900,
          top: "-25%",
          right: "-12%",
          background: `radial-gradient(circle at center, ${product.accent} 0%, transparent 62%)`,
          opacity: 0.16,
          filter: "blur(40px)",
        }}
      />
      {/* Faint vertical rule grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: "linear-gradient(90deg, transparent calc(100% - 1px), white calc(100% - 1px))",
          backgroundSize: "12.5% 100%",
        }}
      />

      <div className="relative z-10 h-full px-6 sm:px-8 md:px-16 lg:px-24 pt-36 md:pt-32 pb-12 flex flex-col">
        {/* Top eyebrow row */}
        <div className="flex items-end justify-between mb-10 md:mb-14">
          <div className="flex items-baseline gap-6">
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.32em] text-white/45">
              {String(active + 1).padStart(2, "0")} / {String(PRODUCTS_LIST.length).padStart(2, "0")}
            </span>
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.32em] text-white/45">
              {t("products.eyebrow") || "Products"}
            </span>
          </div>
          <span className="hidden md:block font-mono text-[0.6rem] uppercase tracking-[0.28em] text-white/35 max-w-[20rem] text-right">
            {t("products.intro")}
          </span>
        </div>

        {/* Editorial split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 flex-1 min-h-0">
          {/* LEFT — Index */}
          <ol className="lg:col-span-5 xl:col-span-5 flex flex-col justify-between border-t border-white/10">
            {PRODUCTS_LIST.map((p, i) => {
              const isActive = i === active;
              return (
                <li key={p.slug} className="border-b border-white/10">
                  <button
                    type="button"
                    data-testid={`products-item-${i + 1}`}
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onClick={() => navigate(`/products/${p.slug}`)}
                    className="group w-full flex items-center justify-between gap-6 py-5 md:py-6 text-left transition-colors"
                  >
                    <div className="flex items-baseline gap-6 md:gap-8 min-w-0">
                      <span
                        className="font-mono text-[0.65rem] uppercase tracking-[0.3em] transition-colors"
                        style={{ color: isActive ? product.accent : "rgba(255,255,255,0.35)" }}
                      >
                        /0{i + 1}
                      </span>
                      <h3
                        className="font-display leading-none truncate transition-all duration-500"
                        style={{
                          fontSize: "clamp(1.6rem, 3.4vw, 2.9rem)",
                          letterSpacing: "-0.02em",
                          fontWeight: 600,
                          color: isActive ? "#fff" : "rgba(255,255,255,0.55)",
                          transform: isActive ? "translateX(6px)" : "translateX(0)",
                        }}
                      >
                        {p.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-4 flex-none">
                      <span
                        className="hidden md:inline-block font-mono text-[0.6rem] uppercase tracking-[0.28em] transition-colors"
                        style={{ color: isActive ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.3)" }}
                      >
                        {p.code}
                      </span>
                      <ArrowUpRight
                        size={18}
                        className="transition-all duration-500"
                        style={{
                          color: isActive ? product.accent : "rgba(255,255,255,0.3)",
                          transform: isActive ? "translate(0,0) rotate(0deg)" : "translate(-6px,6px) rotate(-30deg)",
                        }}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>

          {/* RIGHT — Spotlight panel */}
          <div className="lg:col-span-7 xl:col-span-7 flex flex-col justify-between min-h-0">
            <div className="relative flex-1 min-h-0">
              {/* Giant code mark — watermark */}
              <div
                key={product.slug + "-code"}
                aria-hidden
                className="absolute -top-6 right-0 font-display select-none pointer-events-none transition-opacity duration-700"
                style={{
                  fontSize: "clamp(8rem, 18vw, 17rem)",
                  fontWeight: 800,
                  lineHeight: 0.78,
                  letterSpacing: "-0.06em",
                  color: product.accent,
                  opacity: 0.08,
                }}
              >
                {product.code}
              </div>

              <div className="relative z-10 pt-4 md:pt-6 flex flex-col gap-6 md:gap-8 max-w-[36rem]">
                <div key={product.slug + "-meta"} className="flex items-center gap-3 fade-swap">
                  <span
                    className="font-mono text-[0.6rem] uppercase tracking-[0.28em] px-2.5 py-1 rounded-sm"
                    style={{ background: product.accent, color: isLightAccent ? "#0c0e14" : "#fff" }}
                  >
                    {product.code}
                  </span>
                  {product.fullName && (
                    <span className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-white/55">
                      {product.fullName}
                    </span>
                  )}
                </div>

                <p
                  key={product.slug + "-tag"}
                  className="font-display fade-swap"
                  style={{
                    fontSize: "clamp(1.3rem, 2.1vw, 1.75rem)",
                    lineHeight: 1.25,
                    letterSpacing: "-0.01em",
                    color: "rgba(255,255,255,0.92)",
                    fontWeight: 500,
                  }}
                >
                  {product.tagline[lang]}
                </p>

                <ul key={product.slug + "-caps"} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 fade-swap">
                  {product.capabilities[lang].slice(0, 4).map((c) => (
                    <li key={c} className="flex items-baseline gap-3 text-sm text-white/65 leading-relaxed">
                      <span
                        aria-hidden
                        className="inline-block w-1.5 h-1.5 rounded-full flex-none translate-y-[5px]"
                        style={{ background: product.accent }}
                      />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-between gap-6 pt-6 mt-6 border-t border-white/10">
              <div className="flex items-center gap-2 font-mono text-[0.6rem] uppercase tracking-[0.32em] text-white/40">
                <span className="inline-block w-6 h-px bg-white/40" />
                {PRODUCTS_LIST.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Product ${i + 1}`}
                    onClick={() => setActive(i)}
                    className="w-2 h-2 rounded-full transition-all duration-300"
                    style={{
                      background: i === active ? product.accent : "rgba(255,255,255,0.18)",
                      transform: i === active ? "scale(1.4)" : "scale(1)",
                    }}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => navigate(`/products/${product.slug}`)}
                data-testid={`products-cta-${product.slug}`}
                className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.28em] text-white/85 hover:text-white"
              >
                <span
                  className="inline-block w-8 h-px transition-all duration-500"
                  style={{ background: product.accent }}
                />
                {lang === "id" ? "Pelajari Produk" : "Open product"}
                <ArrowUpRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Local fade-swap animation */}
      <style>{`
        .fade-swap { animation: pFade 700ms ease-out both; }
        @keyframes pFade {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
